/* eslint-disable no-param-reassign */
/* eslint-disable @typescript-eslint/no-empty-function */

"use client";

import {
  createContext,
  useEffect,
  useState,
  useRef,
  ReactNode,
  useMemo,
} from "react";
import {
  CartInterface,
  CheckoutInterface,
  ProductCartVariantInterface,
} from "@/types/cart";
import { isBothCartVariantSame } from "@/utils/formate-product-variant";
import { useQuery } from "@tanstack/react-query";
import { settingsService } from "@/services/settings";
import { getCheckoutFromItems } from "@/utils/checkout-totals";

export enum SettingKeys {
  shipping_fee = "shipping_fee",
  min_order_amount = "min_order_amount",
}

const initialCheckout: CheckoutInterface = {
  discountPrice: 0,
  price: 0,
  total: 0,
  shipping: 0,
};

export interface CartContextProps {
  cartData: CartInterface[];
  /** When set (e.g. from Buy Now), checkout uses this instead of cart; cart is left unchanged */
  buyNowItems: CartInterface[];
  setBuyNowItems: (items: CartInterface[]) => void;
  clearBuyNowItems: () => void;
  /** For checkout: buyNowItems if any, else cartData */
  effectiveCartData: CartInterface[];
  /** Checkout totals derived from effectiveCartData */
  checkoutData: CheckoutInterface;
  effectiveCheckoutData: CheckoutInterface;
  addToCart: (newProduct: CartInterface) => void;
  incQuantity: (
    id: number,
    variants: ProductCartVariantInterface[],
    quantity: number,
  ) => void;
  decQuantity: (
    id: number,
    variants: ProductCartVariantInterface[],
    quantity: number,
  ) => void;
  removeCartData: (id: number, variants: ProductCartVariantInterface[]) => void;
  setCartData: (data: CartInterface[]) => void;
  updateCartDataLocalStorage: (data: CartInterface[]) => void;
}

const initialValues: CartContextProps = {
  cartData: [],
  buyNowItems: [],
  setBuyNowItems: () => {},
  clearBuyNowItems: () => {},
  effectiveCartData: [],
  checkoutData: initialCheckout,
  effectiveCheckoutData: initialCheckout,
  addToCart: () => {},
  incQuantity: () => {},
  decQuantity: () => {},
  removeCartData: () => {},
  setCartData: () => {},
  updateCartDataLocalStorage: () => {},
};

export const CartContext = createContext<CartContextProps>(initialValues);

function getProductCartKey(product: CartInterface): string {
  const variantKey = product.variants
    ?.map((v) => `${v.groupId.id}-${v.variantId?.[0]?.variantId ?? ""}`)
    .sort()
    .join(",");
  return `${product.slug}-${product.id}-${variantKey ?? ""}`;
}

export const CartProvider = ({ children }: { children: ReactNode }) => {
  const [cartData, setCartData] = useState<CartInterface[]>([]);
  const [buyNowItems, setBuyNowItems] = useState<CartInterface[]>([]);
  const [checkoutData, setCheckoutData] =
    useState<CheckoutInterface>(initialCheckout);
  const lastAddKeyRef = useRef<string | null>(null);

  const clearBuyNowItems = () => setBuyNowItems([]);

  const effectiveCartData = useMemo(
    () => (buyNowItems.length > 0 ? buyNowItems : cartData),
    [buyNowItems, cartData],
  );

  const updateCartDataLocalStorage = (data: CartInterface[]) => {
    localStorage.setItem("Cart", JSON.stringify(data));
    window.dispatchEvent(new Event("storage"));
  };

  const { data: shippingSettings } = useQuery([SettingKeys.shipping_fee], () =>
    settingsService.get(SettingKeys.shipping_fee),
  );
  const { data: minOrderAmount } = useQuery(
    [SettingKeys.min_order_amount],
    () => settingsService.get(SettingKeys.min_order_amount),
  );

  const shippingValue = useMemo(() => {
    if (shippingSettings?.data) {
      return Number(shippingSettings.data.value);
    }
    return 0;
  }, [shippingSettings?.data]);

  const minOrderValue = useMemo(() => {
    if (minOrderAmount?.data) {
      return Number(minOrderAmount.data.value);
    }
    return 0;
  }, [minOrderAmount?.data]);

  const addToCart = (newProduct: CartInterface) => {
    const { slug, id, variants } = newProduct;
    const addKey = getProductCartKey(newProduct);

    setCartData((prev) => {
      const duplicateProductIndex = prev.findIndex(
        (item) =>
          item.slug === slug &&
          item.id === id &&
          isBothCartVariantSame(item.variants, variants),
      );

      if (duplicateProductIndex >= 0) {
        const productVariant = prev[duplicateProductIndex].variants;
        const newVariant = newProduct.variants;

        const isSame = isBothCartVariantSame(productVariant, newVariant);

        if (!isSame) {
          const updatedData = [...prev, { ...newProduct }];
          updateCartDataLocalStorage(updatedData);
          return updatedData;
        }

        if (lastAddKeyRef.current === addKey) {
          return prev;
        }

        lastAddKeyRef.current = addKey;
        const updatedData = prev.map((item, index) =>
          index === duplicateProductIndex
            ? {
                ...item,
                quantity: item.quantity + (newProduct.quantity ?? 1),
              }
            : item,
        );
        updateCartDataLocalStorage(updatedData);
        return updatedData;
      }

      if (!variants) {
        newProduct.variants = [];
      }

      lastAddKeyRef.current = addKey;
      const updatedData = [...prev, { ...newProduct }];
      updateCartDataLocalStorage(updatedData);
      return updatedData;
    });

    setTimeout(() => {
      lastAddKeyRef.current = null;
    }, 100);
  };

  const incQuantity = (
    id: number,
    variants: ProductCartVariantInterface[],
    quantity: number,
  ) => {
    setCartData((prev) => {
      const updatedData = prev.map((item) => {
        if (item.id === id && isBothCartVariantSame(item.variants, variants)) {
          const newItem = { ...item };
          newItem.quantity = quantity;
          return newItem;
        }
        return item;
      });
      updateCartDataLocalStorage(updatedData);
      return updatedData;
    });
  };

  const decQuantity = (
    id: number,
    variants: ProductCartVariantInterface[],
    quantity: number,
  ) => {
    setCartData((prev) => {
      const updatedData = prev.map((item) => {
        if (item.id === id && isBothCartVariantSame(item.variants, variants)) {
          const newItem = { ...item };
          if (quantity > 0) {
            newItem.quantity = quantity;
          }
          return newItem;
        }
        return item;
      });
      updateCartDataLocalStorage(updatedData);
      return updatedData;
    });
  };

  const removeCartData = (
    id: number,
    variants: ProductCartVariantInterface[],
  ) => {
    setCartData((prev) => {
      const updatedData = prev.filter(
        (item) =>
          !(item.id === id && isBothCartVariantSame(item.variants, variants)),
      );

      updateCartDataLocalStorage(updatedData);
      return updatedData;
    });
  };

  const checkoutSettings = useMemo(
    () => ({ shippingValue, minOrderValue }),
    [shippingValue, minOrderValue],
  );

  const checkLocalStorage = () => {
    setCartData(() => {
      const storedCartData = localStorage.getItem("Cart");
      const parsedCartData: CartInterface[] = storedCartData
        ? JSON.parse(storedCartData)
        : [];

      return parsedCartData;
    });
  };

  const handleStorageChange = () => {
    checkLocalStorage();
  };

  useEffect(() => {
    checkLocalStorage();

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  useEffect(() => {
    setCheckoutData(getCheckoutFromItems(cartData, checkoutSettings));
  }, [cartData, checkoutSettings]);

  const effectiveCheckoutData = useMemo(
    () => getCheckoutFromItems(effectiveCartData, checkoutSettings),
    [effectiveCartData, checkoutSettings],
  );

  const value = useMemo(
    () => ({
      cartData,
      buyNowItems,
      setBuyNowItems,
      clearBuyNowItems,
      effectiveCartData,
      checkoutData,
      effectiveCheckoutData,
      addToCart,
      incQuantity,
      decQuantity,
      removeCartData,
      setCartData,
      updateCartDataLocalStorage,
    }),
    [
      cartData,
      buyNowItems,
      checkoutData,
      effectiveCartData,
      effectiveCheckoutData,
    ],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
